import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSafeRedirectUrl } from '../tenant/safeRedirect';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useAuth } from '../auth/AuthContext';
import { useColorMode } from '../ThemeContext';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { db, appCheckHeaders } from '../firebase';

const DISBURSEMENTS_PAGE_SIZE = 50;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

async function ownerFetch(path: string, token: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  const ac = await appCheckHeaders();
  for (const [k, v] of Object.entries(ac)) headers.set(k, v);
  return fetch(`${BACKEND_URL}${path}`, { ...init, headers });
}

declare global {
  interface Window {
    dwolla: {
      configure: (env: 'sandbox' | 'production') => void;
      iav: {
        start: (
          token: string,
          options: {
            container: string;
            stylesheets?: string[];
            microDeposits?: boolean;
            fallbackToMicroDeposits?: boolean;
          },
          callback: (err: Error | null, res?: unknown) => void,
        ) => void;
      };
    };
  }
}

interface PaymentStatus {
  configured: boolean;
  fundingSourceAttached?: boolean;
  dwollaCustomerId?: string;
}

interface DisbursementRow {
  id: string;
  amountCents: number;
  status?: string;
  ownerReviewFlag?: boolean;
  createdAt?: { seconds: number } | string;
}

type Phase = 'loading' | 'idle' | 'embedded' | 'processing' | 'done' | 'error';

export default function Settings() {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const { mode, toggleMode } = useColorMode();

  const [status, setStatus] = useState<PaymentStatus | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [consented, setConsented] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [iavToken, setIavToken] = useState<string | null>(null);
  const [iavEnv, setIavEnv] = useState<'sandbox' | 'production'>('sandbox');
  const [confirmNonce, setConfirmNonce] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  const [disburseAmount, setDisburseAmount] = useState('');
  const [disburseLoading, setDisburseLoading] = useState(false);
  const [disburseResult, setDisburseResult] = useState<{ status: string } | null>(null);
  const [disburseError, setDisburseError] = useState<string | null>(null);
  // Idempotency key per-attempt: generated when user clicks Transfer, reused
  // on auto-retry, regenerated only after success or amount change.
  const disburseKeyRef = useRef<string | null>(null);

  const [disbursements, setDisbursements] = useState<DisbursementRow[] | null>(null);
  const [disbursementsCursor, setDisbursementsCursor] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [disbursementsHasMore, setDisbursementsHasMore] = useState(false);
  const [disbursementsLoadingMore, setDisbursementsLoadingMore] = useState(false);
  const [payoutStatusUnavailable, setPayoutStatusUnavailable] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!user) return;
    const token = await getIdToken(false);
    if (!token) return;
    tokenRef.current = token;
    try {
      const res = await ownerFetch('/owner/payment-setup', token);
      if (!res.ok) {
        setStatus({ configured: false });
        setPhase('idle');
        return;
      }
      const data: PaymentStatus = await res.json();
      setStatus(data);
      setPhase('idle');
    } catch {
      setStatus({ configured: false });
      setPhase('idle');
    }
  }, [user, getIdToken]);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  const fetchDisbursements = useCallback(async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, 'clients', user.uid, 'disbursements'),
        orderBy('createdAt', 'desc'),
        limit(DISBURSEMENTS_PAGE_SIZE),
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as Omit<DisbursementRow, 'id'>) }),
      );
      setDisbursements(rows);
      setPayoutStatusUnavailable(false);
      const submitted = rows.find((d) => d.id === disburseKeyRef.current);
      if (submitted && ['pending', 'paid', 'failed'].includes(submitted.status ?? '')) {
        setDisburseResult(null);
        disburseKeyRef.current = null;
      }
      setDisbursementsCursor(snap.docs[snap.docs.length - 1] ?? null);
      setDisbursementsHasMore(snap.docs.length === DISBURSEMENTS_PAGE_SIZE);
    } catch {
      setPayoutStatusUnavailable(true);
      setDisbursementsCursor(null);
      setDisbursementsHasMore(false);
    }
  }, [user]);

  useEffect(() => { void fetchDisbursements(); }, [fetchDisbursements]);

  const loadMoreDisbursements = useCallback(async () => {
    if (!user || !disbursementsCursor || disbursementsLoadingMore) return;
    setDisbursementsLoadingMore(true);
    try {
      const q = query(
        collection(db, 'clients', user.uid, 'disbursements'),
        orderBy('createdAt', 'desc'),
        startAfter(disbursementsCursor),
        limit(DISBURSEMENTS_PAGE_SIZE),
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as Omit<DisbursementRow, 'id'>) }),
      );
      setDisbursements((prev) => (prev ? [...prev, ...rows] : rows));
      setDisbursementsCursor(snap.docs[snap.docs.length - 1] ?? disbursementsCursor);
      setDisbursementsHasMore(snap.docs.length === DISBURSEMENTS_PAGE_SIZE);
    } catch {
      setDisbursementsHasMore(false);
    } finally {
      setDisbursementsLoadingMore(false);
    }
  }, [user, disbursementsCursor, disbursementsLoadingMore]);

  const handleDisburse = async () => {
    const dollars = parseFloat(disburseAmount);
    if (isNaN(dollars) || dollars <= 0) {
      setDisburseError('Enter a positive dollar amount');
      return;
    }
    const amountCents = Math.round(dollars * 100);
    setDisburseLoading(true);
    setDisburseError(null);
    setDisburseResult(null);
    if (!disburseKeyRef.current) {
      disburseKeyRef.current =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 18)}`;
    }
    try {
      const token = tokenRef.current ?? (await getIdToken(false));
      if (!token) throw new Error('Not signed in');
      const res = await ownerFetch('/owner/disburse', token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': disburseKeyRef.current,
        },
        body: JSON.stringify({ amountCents }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDisburseError(res.status === 409 ? 'This payout request was already submitted. Refresh payout status.'
          : res.status === 502 ? 'Payout failed. Reserved funds were returned to your available balance.'
          : 'Payout request could not be accepted. Refresh payout status before trying again.');
        void fetchDisbursements();
        return;
      }
      const unresolved = res.status === 202 || body.status === 'unresolved';
      setDisburseResult({ status: unresolved ? 'unresolved' : body.status });
      setDisburseAmount('');
      if (!unresolved) disburseKeyRef.current = null;
      void fetchDisbursements();
    } catch {
      setDisburseResult({ status: 'unresolved' });
      void fetchDisbursements();
    } finally {
      setDisburseLoading(false);
    }
  };

  const handleConnect = async () => {
    setPhase('loading');
    setErrorMsg('');
    try {
      const token = tokenRef.current ?? (await getIdToken(false));
      if (!token) throw new Error('Not signed in');
      const res = await ownerFetch('/owner/payment-setup', token, {
        method: 'POST',
        body: JSON.stringify({ consented: true }),
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`Setup failed (${res.status})`);
      const data = await res.json();
      if (data.alreadyConfigured) {
        await loadStatus();
        return;
      }
      if (data.mode === 'redirect') {
        // Stripe Connect Express onboarding: backend returns a hosted URL
        // (connect.stripe.com) for the owner to complete KYC + bank linkage.
        // Guard with the safeRedirect allowlist before following (Spec 037).
        if (!isSafeRedirectUrl(data.payload?.url)) {
          throw new Error('Invalid onboarding link');
        }
        window.location.href = data.payload.url as string;
        return;
      }
      if (data.mode === 'embedded') {
        setIavToken(data.payload.token as string);
        setIavEnv((data.payload.env as 'sandbox' | 'production') ?? 'sandbox');
        setConfirmNonce(data.confirmNonce as string ?? null);
        setPhase('embedded');
      } else {
        throw new Error('Unexpected setup response');
      }
    } catch (err: any) {
      setErrorMsg(err?.message ?? 'Failed to start account setup. Please try again.');
      setPhase('error');
    }
  };

  useEffect(() => {
    if (phase !== 'embedded' || !iavToken) return;

    const script = document.createElement('script');
    // SRI hash pins this specific build of dwolla.js (F12).
    // If Dwolla updates the CDN file, this hash will no longer match and the
    // widget will fail to load — update the hash when upgrading Dwolla JS.
    script.src = 'https://cdn.dwolla.com/1/dwolla.js';
    script.integrity = 'sha384-hyo2lOoNXE0qcFn+WHSj/5RZdaxyYxP3ftiz2tP7maynEbDKsNc1lZOthtK9YIOR';
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      window.dwolla.configure(iavEnv);
      window.dwolla.iav.start(
        iavToken,
        { container: 'ownerIavContainer', microDeposits: false, fallbackToMicroDeposits: false },
        (err) => {
          if (err) {
            setErrorMsg('Account setup failed. Please try again.');
            setPhase('error');
            return;
          }
          setPhase('processing');
          void (async () => {
            try {
              const tok = tokenRef.current ?? (await getIdToken(false));
              if (tok) {
                await ownerFetch('/owner/payment-setup/confirm', tok, {
                  method: 'POST',
                  body: JSON.stringify({ confirmNonce }),
                  headers: { 'Content-Type': 'application/json' },
                });
              }
            } catch {
              // non-fatal — webhook is authoritative
            }
            await loadStatus();
          })();
        },
      );
    };
    script.onerror = () => {
      setErrorMsg('Failed to load account widget. Please refresh and try again.');
      setPhase('error');
    };
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) document.head.removeChild(script);
      delete (window as any).dwolla;
    };
  }, [phase, iavToken, iavEnv, confirmNonce, getIdToken, loadStatus]);

  const isActive = status?.configured && status?.fundingSourceAttached;

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Tooltip title="Go back">
            <IconButton
              color="inherit"
              onClick={() => navigate('/')}
              edge="start"
              aria-label="Go back"
              sx={{ mr: 1, opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <ArrowBackOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontFamily: "'Fraunces', serif", fontWeight: 600, letterSpacing: '-0.5px' }}
          >
            Utilibill
          </Typography>
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              color="inherit"
              onClick={toggleMode}
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4, pb: 8 }}>
        <Typography
          variant="h4"
          sx={{ fontFamily: "'Fraunces', serif", fontWeight: 700, color: 'text.primary', mb: 0.5 }}
        >
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Manage your payment account and preferences.
        </Typography>

        {/* Payment account section */}
        <Paper
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 3 }}
        >
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                <AccountBalanceOutlinedIcon sx={{ fontSize: 17 }} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Payment account</Typography>
                <Typography variant="caption" color="text.secondary">
                  Receive ACH payments from tenants
                </Typography>
              </Box>
              {status !== null && (
                <Chip
                  icon={isActive
                    ? <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />
                    : <ErrorOutlineIcon sx={{ fontSize: '14px !important' }} />}
                  label={isActive ? 'Active' : 'Not configured'}
                  color={isActive ? 'success' : 'warning'}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          <Box sx={{ px: 2.5, py: 2.5 }}>
            {phase === 'loading' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {phase === 'processing' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Connecting your account…</Typography>
              </Box>
            )}

            {phase === 'error' && (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
                <Button variant="outlined" onClick={() => { setPhase('idle'); setErrorMsg(''); setConsented(false); }}>
                  Try again
                </Button>
              </>
            )}

            {phase === 'idle' && !isActive && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                  By connecting your bank account, you authorize Utilibill to electronically
                  credit your account with tenant utility payments collected on your behalf.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
                  Payment processing is provided by{' '}
                  <Link href="https://stripe.com" target="_blank" rel="noopener noreferrer">Stripe</Link>.
                  By proceeding you agree to Stripe&apos;s{' '}
                  <Link href="https://stripe.com/legal/ssa" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                  {' '}and{' '}
                  <Link href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consented}
                      onChange={(e) => setConsented(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I authorize electronic credits to my bank account as described above.
                    </Typography>
                  }
                  sx={{ mb: 2.5, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
                />
                <Button
                  variant="contained"
                  disabled={!consented}
                  onClick={() => void handleConnect()}
                >
                  Connect bank account
                </Button>
              </>
            )}

            {phase === 'idle' && isActive && (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccountBalanceOutlinedIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Bank account connected
                  </Typography>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => { setConsented(false); void handleConnect(); }}
                >
                  Reconfigure
                </Button>
              </Stack>
            )}

            {phase === 'embedded' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Securely connect your bank account to receive tenant payments.
                </Typography>
                <div id="ownerIavContainer" />
              </Box>
            )}
          </Box>
        </Paper>

        {/* Disbursement section — visible only when bank is connected */}
        {isActive && (
          <>
            <Divider sx={{ my: 3 }} />
            <Paper
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 3 }}
            >
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      flexShrink: 0,
                    }}
                  >
                    <SendOutlinedIcon sx={{ fontSize: 17 }} />
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Withdraw funds</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Transfer collected payments to your bank account
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box sx={{ px: 2.5, py: 2.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
                  Enter the amount to transfer from the Utilibill collection account to your connected bank account.
                </Typography>
                {disburseResult && (
                  <Alert severity={disburseResult.status === 'unresolved' ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    {disburseResult.status === 'unresolved'
                      ? 'Payout outcome unresolved. Funds remain reserved. Do not initiate a replacement payout.'
                      : 'Payout submitted. Awaiting bank confirmation.'}
                  </Alert>
                )}
                {disburseError && (
                  <Alert severity="error" sx={{ mb: 2 }}>{disburseError}</Alert>
                )}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <TextField
                    label="Amount"
                    type="number"
                    size="small"
                    value={disburseAmount}
                    onChange={(e) => {
                      setDisburseAmount(e.target.value);
                      setDisburseResult(null);
                      setDisburseError(null);
                      // Amount change → fresh idempotency key on next click.
                      disburseKeyRef.current = null;
                    }}
                    inputProps={{ min: 0.01, step: 0.01 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={{ width: 160 }}
                    disabled={disburseLoading || disburseResult?.status === 'unresolved'}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    disabled={disburseLoading || !disburseAmount || payoutStatusUnavailable || disbursements === null
                      || disburseResult?.status === 'unresolved'
                      || disbursements?.some((d) => d.ownerReviewFlag || ['unresolved', 'payout_pending'].includes(d.status ?? ''))}
                    onClick={() => void handleDisburse()}
                    startIcon={disburseLoading ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                  >
                    {disburseLoading ? 'Transferring…' : 'Transfer to bank'}
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {/* Transfer history */}
            <Paper
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
            >
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Transfer history</Typography>
                <Typography variant="caption" color="text.secondary">
                  {disbursements && disbursements.length > 0
                    ? `${disbursements.length} withdrawal${disbursements.length === 1 ? '' : 's'} shown`
                    : 'Recent withdrawals'}
                </Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 2 }}>
                <Button onClick={() => { void fetchDisbursements(); }} size="small">Refresh payout status</Button>
                {payoutStatusUnavailable && (
                  <Alert severity="warning">Payout status could not be checked. Do not initiate a replacement payout.</Alert>
                )}
                {disbursements === null && !payoutStatusUnavailable && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} />
                  </Box>
                )}
                {disbursements !== null && disbursements.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No transfers yet.</Typography>
                )}
                {disbursements !== null && disbursements.length > 0 && (
                  <Stack spacing={1}>
                    {disbursements.map((d) => {
                      const date = d.createdAt
                        ? (typeof d.createdAt === 'object' && 'seconds' in d.createdAt
                            ? new Date(d.createdAt.seconds * 1000)
                            : new Date(d.createdAt as string))
                        : null;
                      const status = d.ownerReviewFlag ? 'review' : d.status ?? 'unresolved';
                      const statusColor: 'warning' | 'success' | 'error' =
                        ['paid', 'completed'].includes(status) ? 'success' : status === 'failed' ? 'error' : 'warning';
                      const statusLabel = ['paid', 'completed'].includes(status) ? 'Paid'
                        : status === 'failed' ? 'Failed' : status === 'pending' ? 'Awaiting bank confirmation'
                        : status === 'review' ? 'Payout needs review. Do not initiate a replacement payout.'
                        : 'Payout outcome unresolved. Funds remain reserved. Do not initiate a replacement payout.';
                      const statusIcon =
                        statusColor === 'success' ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: '12px !important' }} />
                        ) : statusColor === 'error' ? (
                          <ErrorOutlineIcon sx={{ fontSize: '12px !important' }} />
                        ) : (
                          <HourglassEmptyOutlinedIcon sx={{ fontSize: '12px !important' }} />
                        );
                      const statusTextColor =
                        statusColor === 'success'
                          ? 'success.dark'
                          : statusColor === 'error'
                          ? 'error.dark'
                          : 'warning.dark';
                      return (
                        <Box
                          key={d.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 0.75,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ${(d.amountCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {date ? date.toLocaleString() : '—'}
                            </Typography>
                          </Box>
                          <Chip
                            icon={statusIcon}
                            label={statusLabel}
                            color={statusColor}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 'auto',
                              '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                              fontSize: '0.65rem',
                              flexShrink: 0,
                              color: statusTextColor,
                              borderColor: statusTextColor,
                              '& .MuiChip-icon': { color: statusTextColor },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                {disbursements !== null && disbursements.length > 0 && disbursementsHasMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => void loadMoreDisbursements()}
                      disabled={disbursementsLoadingMore}
                      startIcon={
                        disbursementsLoadingMore ? <CircularProgress size={14} color="inherit" /> : undefined
                      }
                    >
                      {disbursementsLoadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Notifications section (future) */}
        <Paper
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', opacity: 0.5 }}
        >
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>Notifications</Typography>
            <Typography variant="caption" color="text.secondary">
              Email preferences — coming soon
            </Typography>
          </Box>
        </Paper>
      </Container>
    </>
  );
}
